/**
 * v1 rate interval wire ↔ domain (`BookingRateInterval`, `ExpenseRateInterval`).
 * Import from `*.http.ts`, services, or UI that reads/writes legacy interval strings.
 */

import { RateInterval } from '__types__/enums';
import type { BookingRateInterval } from 'v5/core/booking-billing/model';
import type { ExpenseRateInterval } from 'v5/core/expense-billing/model';

export type ResourcingIntervalWireInput =
  | BookingRateInterval
  | ExpenseRateInterval
  | string
  | null
  | undefined;

export function parseBookingRateInterval(
  v: ResourcingIntervalWireInput
): BookingRateInterval {
  if (v === 'hourly' || v === 'HOURLY') return 'hourly';
  if (v === 'fixed' || v === 'FIXED') return 'fixed';
  return 'daily';
}

export function parseExpenseRateInterval(
  v: ResourcingIntervalWireInput
): ExpenseRateInterval {
  if (v === 'hourly' || v === 'HOURLY') return 'hourly';
  if (v === 'fixed' || v === 'FIXED') return 'fixed';
  if (v === 'perItem' || v === 'ITEM') return 'perItem';
  return 'daily';
}

/** Map expense / qty-unit interval to {@link BookingRateInterval} on resource slot plans. */
export function expenseIntervalToResourcePlanInterval(
  v: ResourcingIntervalWireInput
): BookingRateInterval {
  const interval = parseExpenseRateInterval(v);
  return interval === 'perItem' ? 'fixed' : interval;
}

/** Legacy v1 API string values (`HOURLY`, `DAILY`, …). */
export function bookingRateIntervalToWire(
  interval: BookingRateInterval
): string {
  switch (interval) {
    case 'hourly':
      return 'HOURLY';
    case 'fixed':
      return 'FIXED';
    default:
      return 'DAILY';
  }
}

export function expenseRateIntervalToWire(
  interval: ExpenseRateInterval
): string {
  switch (interval) {
    case 'hourly':
      return 'HOURLY';
    case 'fixed':
      return 'FIXED';
    case 'perItem':
      return 'ITEM';
    default:
      return 'DAILY';
  }
}

/** v1 {@link RateInterval} enum for mutation payloads. */
export function bookingRateIntervalToWireEnum(
  interval: BookingRateInterval
): RateInterval {
  switch (interval) {
    case 'hourly':
      return RateInterval.Hourly;
    case 'fixed':
      return RateInterval.Fixed;
    default:
      return RateInterval.Daily;
  }
}

export function expenseRateIntervalToWireEnum(
  interval: ExpenseRateInterval
): RateInterval {
  switch (interval) {
    case 'hourly':
      return RateInterval.Hourly;
    case 'fixed':
      return RateInterval.Fixed;
    case 'perItem':
      return RateInterval.Item;
    default:
      return RateInterval.Daily;
  }
}
